// Sugalarm — voice intent → spoken string mapping (cycle 101).
//
// Pure + testable. The SPA answers the five spoken queries from its
// in-memory state (latest reading / IOB / treatments) so they work
// OFFLINE on an always-loaded kiosk. `getGlucose` composes through the
// cycle-309 `lib/utterance.ts` pipeline; the rest are built here.
//
// Half-unit speech: dose amounts (the logged dose, last dose, confirm-
// back) are spoken in WORD form — 6.5 → "six and a half units", never a
// raw "six point five" / "6.5" readback.
//
// CYCLE 387 extends that to EVERY number this module speaks — the IOB
// total, durations, ages and carb grams — and moves the word tables onto
// the shared `spellNumber` seam, which the server's call rail and the
// cycle-309 utterance pipeline also use. Rationale: the live TTS engine
// mis-reads bare numerals, and half a spoken sentence in words with the
// other half in digits is the worst of both. `intWord` used to top out at
// 50 and silently fall back to digits above it; the shared speller covers
// 0–999, so "ninety minutes" no longer comes out as "90".

import { IOB_DURATION_MS, iobTimeRemainingMs, remainingFraction } from "./iob";
import { spellValue } from "./spellNumber";
import type { Treatment } from "./types";

// ─── number / unit speech ─────────────────────────────────────────────

/** Integer → English words. Thin alias over the shared cycle-387 speller
 *  (which covers 0–999 and falls back to the digits beyond that), kept as a
 *  named export because the dose helpers below read better with it. */
export function intWord(n: number): string {
  return spellValue(n);
}

/** A dose amount in half-unit steps spoken naturally: 0.5 → "half a unit",
 *  1 → "one unit", 6.5 → "six and a half units", 15 → "fifteen units". */
export function speakDoseUnits(n: number): string {
  if (n === 0.5) return "half a unit";
  const whole = Math.floor(n);
  const hasHalf = n - whole === 0.5;
  const wholeWord = intWord(whole);
  if (hasHalf) return `${wholeWord} and a half units`;
  return `${wholeWord} ${whole === 1 ? "unit" : "units"}`;
}

/** The continuous IOB total, spoken in words to one decimal place —
 *  "two point four units". Rounded first so we never read "2.40000001". */
function speakIobUnits(n: number): string {
  const r = Math.round(n * 10) / 10;
  const s = Number.isInteger(r) ? spellValue(r) : spellValue(r, 1);
  return `${s} ${r === 1 ? "unit" : "units"}`;
}

function capitalize(s: string): string {
  return s.length ? s[0]!.toUpperCase() + s.slice(1) : s;
}

/** Spoken duration: "about 2 hours and 10 minutes" / "about 35 minutes". */
function speakDuration(ms: number): string {
  const totalMin = Math.max(0, Math.round(ms / 60_000));
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  const parts: string[] = [];
  if (h > 0) parts.push(`${spellValue(h)} ${h === 1 ? "hour" : "hours"}`);
  if (m > 0) parts.push(`${spellValue(m)} ${m === 1 ? "minute" : "minutes"}`);
  if (parts.length === 0) return "less than a minute";
  return parts.join(" and ");
}

/** Relative age, spoken: "just now" / "about 40 minutes ago". */
function speakAgo(takenAt: number, now: number): string {
  const ms = now - takenAt;
  if (ms < 60_000) return "just now";
  return `about ${speakDuration(ms)} ago`;
}

// ─── active-insulin helpers (shared decay with the rest of the app) ────

interface ActiveBolus {
  units: number;
  takenAt: number;
}

function activeBoluses(treatments: Treatment[], now: number): ActiveBolus[] {
  const cutoff = now - IOB_DURATION_MS;
  return treatments
    .filter(
      (t) =>
        t.event_type === "insulin" &&
        t.insulin_u != null &&
        new Date(t.timestamp).getTime() >= cutoff,
    )
    .map((t) => ({
      units: Number(t.insulin_u),
      takenAt: new Date(t.timestamp).getTime(),
    }));
}

/** Total IOB across ALL active boluses (not capped at the 2 rings) — the
 *  same linear decay `iob.ts` uses, never a parallel calc. */
export function totalIOBUnits(treatments: Treatment[], now: number = Date.now()): number {
  return activeBoluses(treatments, now).reduce(
    (sum, b) => sum + b.units * remainingFraction(b.takenAt, now),
    0,
  );
}

// ─── intent answer strings ────────────────────────────────────────────

export function getIOBText(treatments: Treatment[], now: number = Date.now()): string {
  const total = totalIOBUnits(treatments, now);
  if (total < 0.05) return "You have no insulin on board right now.";
  return `You have ${speakIobUnits(total)} on board.`;
}

export function getInsulinTimeLeftText(
  treatments: Treatment[],
  now: number = Date.now(),
): string {
  const active = activeBoluses(treatments, now);
  if (active.length === 0) {
    return "No insulin on board, so nothing's counting down.";
  }
  // Cycle 104: the remaining window is the cycle-104 `iobTimeRemainingMs`
  // (max remaining decay window across active boluses), the SAME function
  // the BolusRings countdown line displays — so the spoken answer and the
  // on-screen countdown can never disagree.
  const remainingMs = iobTimeRemainingMs(active, now);
  return `About ${speakDuration(remainingMs)} until your insulin clears.`;
}

export function getLastDoseText(treatments: Treatment[], now: number = Date.now()): string {
  const doses = treatments
    .filter((t) => t.event_type === "insulin" && t.insulin_u != null)
    .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
  const last = doses[0];
  if (!last) return "I don't see any recent insulin doses.";
  const units = Number(last.insulin_u);
  const takenAt = new Date(last.timestamp).getTime();
  return `Your last dose was ${speakDoseUnits(units)}, ${speakAgo(takenAt, now)}.`;
}

// ─── confirm-back + result strings (logInsulin) ───────────────────────

export function confirmBackText(units: number): string {
  return `Logging ${speakDoseUnits(units)} — say yes to confirm.`;
}

export function loggedText(units: number, queued: boolean): string {
  const dose = capitalize(speakDoseUnits(units));
  return queued
    ? `${dose} logged — I'll sync it when you're back online.`
    : `${dose} logged.`;
}

export const MISS_TEXT = "Sorry, I didn't catch that.";
export const CANCELLED_TEXT = "Okay, cancelled.";

// ─── carb lookup (cycle 102) ──────────────────────────────────────────

export const CANT_LOOKUP_CARBS_TEXT = "I can't look up carbs right now.";

interface NamedItem {
  name: string;
}

/** A spoken food label from the estimate's item names: "a Big Mac" →
 *  "Big Mac"; up to two items joined, "… and more" beyond that. Null when
 *  the estimate has no items (the sentence then omits the "for …" tail). */
export function foodLabelFromItems(items: NamedItem[]): string | null {
  const names = items.map((i) => i.name?.trim()).filter(Boolean) as string[];
  if (names.length === 0) return null;
  if (names.length === 1) return names[0]!;
  if (names.length === 2) return `${names[0]} and ${names[1]}`;
  return `${names[0]}, ${names[1]} and more`;
}

/** The carbs treatment's `notes`: the estimate's item names, else the
 *  spoken transcript. Capped at 400 chars to match the column. */
export function carbNotesFromEstimate(
  items: NamedItem[],
  transcript: string,
): string {
  const names = items.map((i) => i.name?.trim()).filter(Boolean) as string[];
  const text = names.length > 0 ? names.join(", ") : transcript.trim();
  return text.slice(0, 400);
}

function grams(total: number): string {
  return spellValue(Math.max(0, Math.round(total)));
}

function forFood(label: string | null): string {
  return label ? ` for ${label}` : "";
}

export function carbEstimateText(total: number, foodLabel: string | null): string {
  return `About ${grams(total)} grams of carbs${forFood(foodLabel)}. Want me to log it?`;
}

export function carbLowConfidenceText(
  total: number,
  foodLabel: string | null,
): string {
  return `About ${grams(total)} grams of carbs${forFood(foodLabel)}, but I'm not very sure about that one.`;
}

export function carbLoggedText(total: number, queued: boolean): string {
  return queued
    ? `Logged ${grams(total)} grams of carbs — I'll sync it when you're back online.`
    : `Logged ${grams(total)} grams of carbs.`;
}
